'use client';

import { useMemo, useState } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Badge } from '../../../components/badge';
import { Checkbox } from '../../../components/checkbox';
import { TableGrid, type TableGridRow } from '../../table-grid/TableGrid';
import { humanizeSnakeCase } from './humanize-snake-case';
import { formatSourceLabel } from './format-source-label';
import { scopeBadgeStyle } from './scope-constants';
import { EntityContainer, SkeletonList, ScopeEmptyState } from './scope-shared';
import { ScopeMenu } from './ScopeMenu';
import { EvidenceDrawer } from './EvidenceDrawer';
import type { NetblocksSectionProps, ScopeEvidence } from './scope-types';
import { getNetblockWhoisOrganization } from './netblock-whois.utils';
import { CloudNetblockMeta } from './CloudNetblockMeta';

export function NetblocksSection({
  netblocks,
  isLoading,
  selectedIds,
  isDisabled,
  onToggleSelection,
  onScopeChange,
}: NetblocksSectionProps) {
  const t = useTranslations('designSystem.networkSettings.externalScope');
  const [drawerState, setDrawerState] = useState<{
    title: string;
    evidences: ScopeEvidence[];
  } | null>(null);

  const rows = useMemo<TableGridRow[]>(
    () =>
      netblocks.map((netblock) => ({
        id: netblock.id,
        value: [
          netblock.cidr,
          netblock.sourceType,
          netblock.scope,
          getNetblockWhoisOrganization({ whois: netblock.whois }),
          netblock.whois?.asn,
        ]
          .filter(Boolean)
          .join(' '),
        cells: [
          <HStack key="cidr" gap={2} alignItems="center" minW={0}>
            <Checkbox
              checked={selectedIds.has(netblock.id)}
              onCheckedChange={() => onToggleSelection(netblock.id)}
              size="sm"
              disabled={isDisabled}
            />
            <Box truncate minW={0}>
              {netblock.cidr}
            </Box>
            <CloudNetblockMeta
              cloudProvider={netblock.cloudProvider}
              cloudAccountRef={netblock.cloudAccountRef}
              resourceKind={netblock.resourceKind}
              inventoryLinked={netblock.inventoryLinked}
              cloudAllocationId={netblock.cloudAllocationId}
              deprovisionedAt={netblock.deprovisionedAt}
              unmatchedLabel={t('unmatchedCloudHost')}
            />
          </HStack>,
          formatSourceLabel({ sourceType: netblock.sourceType }),
          netblock.evidences.length > 0 ? (
            <Box
              key="evidence"
              onClick={() =>
                setDrawerState({
                  title: netblock.cidr,
                  evidences: netblock.evidences,
                })
              }
              cursor="pointer"
            >
              <Badge variant="outline" size="sm" colorPalette="gray">
                {netblock.evidences.length}
              </Badge>
            </Box>
          ) : null,
          <HStack
            key="scope"
            gap={1}
            alignItems="center"
            justifyContent="space-between"
            minW={0}
          >
            <Badge
              variant="fill"
              size="sm"
              {...scopeBadgeStyle({ scope: netblock.scope })}
            >
              {humanizeSnakeCase(netblock.scope)}
            </Badge>
            <ScopeMenu
              isDisabled={isDisabled}
              onScopeChange={(scope) =>
                onScopeChange({ id: netblock.id, scope })
              }
            />
          </HStack>,
        ],
      })),
    [netblocks, selectedIds, isDisabled, onToggleSelection, onScopeChange, t],
  );

  if (isLoading) {
    return <SkeletonList count={4} />;
  }

  return (
    <>
      <VStack gap={0} alignItems="stretch">
        <EntityContainer>
          <TableGrid
            columns={[
              { id: 'cidr', title: t('cidr'), width: 'minmax(0, 1fr)' },
              { id: 'source', title: t('source'), width: 'minmax(0, 1fr)' },
              { id: 'evidence', title: t('evidence'), width: '72px' },
              { id: 'scope', title: t('scopeColumn'), width: '124px' },
            ]}
            rows={rows}
            placeholder={t('searchNetblocks')}
            tableMaxHeight="50vh"
            emptyState={<ScopeEmptyState />}
          />
        </EntityContainer>
      </VStack>
      <EvidenceDrawer
        isOpen={drawerState !== null}
        onClose={() => setDrawerState(null)}
        title={drawerState?.title ?? ''}
        evidences={drawerState?.evidences ?? []}
      />
    </>
  );
}
